import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface QuizResult {
  id: string;
  session_id: string;
  travel_style: string;
  budget_preference: string | null;
  activity_level: string | null;
  trip_duration: string | null;
  recommended_tags: string[] | null;
  email: string | null;
  created_at: string | null;
}

interface QuizStats {
  totalSubmissions: number;
  emailCaptureRate: number;
  travelStyleDistribution: Record<string, number>;
  budgetDistribution: Record<string, number>;
  activityDistribution: Record<string, number>;
  durationDistribution: Record<string, number>;
}

export function useQuizAnalytics() {
  return useQuery({
    queryKey: ['quiz-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quiz_results')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const results = data as QuizResult[];
      
      const stats: QuizStats = {
        totalSubmissions: results.length,
        emailCaptureRate: results.length > 0 
          ? (results.filter(r => r.email).length / results.length) * 100 
          : 0,
        travelStyleDistribution: {},
        budgetDistribution: {},
        activityDistribution: {},
        durationDistribution: {},
      };

      results.forEach(result => {
        // Travel style
        if (result.travel_style) {
          stats.travelStyleDistribution[result.travel_style] = 
            (stats.travelStyleDistribution[result.travel_style] || 0) + 1;
        }
        // Budget
        if (result.budget_preference) {
          stats.budgetDistribution[result.budget_preference] = 
            (stats.budgetDistribution[result.budget_preference] || 0) + 1;
        }
        // Activity
        if (result.activity_level) {
          stats.activityDistribution[result.activity_level] = 
            (stats.activityDistribution[result.activity_level] || 0) + 1;
        }
        // Duration
        if (result.trip_duration) {
          stats.durationDistribution[result.trip_duration] = 
            (stats.durationDistribution[result.trip_duration] || 0) + 1;
        }
      });

      return { results, stats };
    },
  });
}

export function useQuizEmails() {
  return useQuery({
    queryKey: ['quiz-emails'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quiz_results')
        .select('email, travel_style, created_at')
        .not('email', 'is', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}
